import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  isPushSupported,
  getPushStatus,
  enablePush,
  disablePush,
  sendTestPush,
} from "@/lib/push";

/**
 * Push notification opt-in card. Drop it on any authenticated page.
 * `description` lets callers tailor the copy for drivers vs customers.
 */
export default function PushToggle({ description = "Get instant alerts on this device." }) {
  const [status, setStatus] = useState({ supported: true, subscribed: false, permission: "default" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = await getPushStatus();
    setStatus(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push notifications disabled");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not disable notifications");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      if (res?.delivered) toast.success("Test notification sent");
      else toast.error("Test could not be delivered — check permission settings");
    } catch (e) {
      toast.error("Test failed");
    } finally {
      setBusy(false);
    }
  };

  if (!isPushSupported()) {
    return (
      <Card className="bg-[#121212] border-white/5" data-testid="push-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/5">
              <BellOff className="w-5 h-5 text-white/60" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Push Notifications</CardTitle>
              <CardDescription className="text-zinc-500">
                Not supported on this browser. Use the installable PWA on Android or Chrome desktop.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  const blocked = status.permission === "denied";

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="push-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${status.subscribed ? "bg-emerald-500/10" : "bg-[#0047FF]/10"}`}>
            {status.subscribed ? (
              <BellRing className="w-5 h-5 text-emerald-400" />
            ) : (
              <Bell className="w-5 h-5 text-[#4D88FF]" />
            )}
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              Push Notifications
              {status.subscribed && (
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">On</Badge>
              )}
            </CardTitle>
            <CardDescription className="text-zinc-500">{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {blocked && (
          <p className="text-xs text-amber-400" data-testid="push-blocked-hint">
            Notifications are blocked in your browser settings. Unblock them for this site and try again.
          </p>
        )}
        {status.subscribed ? (
          <div className="flex gap-2">
            <Button
              onClick={handleTest}
              disabled={busy}
              variant="outline"
              className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800"
              data-testid="push-test-btn"
            >
              Send test
            </Button>
            <Button
              onClick={handleDisable}
              disabled={busy}
              variant="outline"
              className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="push-disable-btn"
            >
              Turn off
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleEnable}
            disabled={busy || blocked}
            className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl border-0"
            data-testid="push-enable-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            {busy ? "Enabling…" : "Enable notifications"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
